import { ReactElement } from "react";
import {
  IconCategory,
  IconChartBar,
  IconCreditCard,
  IconFileExport,
  IconHome,
  IconLockAccess,
  IconPackage,
  IconShoppingCart,
  IconUsers,
} from "@tabler/icons-react";

import {
  BANK_ACCOUNT_PAGE,
  DASHBOARD,
  DEBT_PAGE,
  EXPORT_JOBS_PAGE,
  INVENTORY_PAGE,
  MEASUREMENT_UNIT_PAGE,
  PARTNER_PAGE,
  PERMISSION_PAGE,
  PRODUCT_CATEGORY_PAGE,
  PRODUCT_PAGE,
  PURCHASE_ORDER_PAGE,
  REGION_PAGE,
  REPORT_PAGE,
  SALES_ORDER_PAGE,
  USER_PAGE,
  WAREHOUSE_PAGE,
} from "routes/route.constant";
import { UserRoles } from "constants/types/user";
import { PermissionRequirement } from "constants/types/permission";
import { AppAbility } from "configs/casl";
import { canAccessRequirement } from "utils/helpers/authorization";

export interface NavMenuChild {
  key: string;
  label: string;
  link: string;
  required: PermissionRequirement;
}

export interface NavMenuItem {
  key: string;
  icon: ReactElement;
  label: string;
  link: string;
  children: NavMenuChild[];
  required: PermissionRequirement;
}

export const FULL_MENU: NavMenuItem[] = [
  {
    key: "dashboard",
    icon: <IconHome />,
    label: "Trang chủ",
    link: DASHBOARD,
    children: [],
    required: { fullAccessOnly: true },
  },
  {
    key: "users",
    icon: <IconUsers />,
    label: "Quản lý người dùng",
    link: USER_PAGE,
    children: [],
    required: "user:read",
  },
  {
    key: "permissions",
    icon: <IconLockAccess />,
    label: "Quản lý quyền",
    link: PERMISSION_PAGE,
    children: [],
    required: { fullAccessOnly: true },
  },
  {
    key: "catalog",
    icon: <IconCategory />,
    label: "Danh mục quản lý",
    link: "",
    required: { public: true },
    children: [
      {
        key: "products",
        label: "Mặt hàng",
        link: PRODUCT_PAGE,
        required: "product:read",
      },
      {
        key: "product-categories",
        label: "Nhóm mặt hàng",
        link: PRODUCT_CATEGORY_PAGE,
        required: "product:read",
      },
      {
        key: "warehouses",
        label: "Nhà kho",
        link: WAREHOUSE_PAGE,
        required: "warehouse:read",
      },
      {
        key: "partners",
        label: "Khách hàng / Nhà cung cấp",
        link: PARTNER_PAGE,
        required: "partner:read",
      },
      {
        key: "regions",
        label: "Địa bàn",
        link: REGION_PAGE,
        required: "partner:read",
      },
      {
        key: "measurement-units",
        label: "Đơn vị tính",
        link: MEASUREMENT_UNIT_PAGE,
        required: "product:read",
      },
      {
        key: "bank-accounts",
        label: "Tài khoản ngân hàng",
        link: BANK_ACCOUNT_PAGE,
        required: "payment_journal:read",
      },
    ],
  },
  {
    key: "orders",
    icon: <IconShoppingCart />,
    label: "Đơn hàng",
    link: "",
    required: { public: true },
    children: [
      {
        key: "purchase-orders",
        label: "Đơn mua hàng / Nhập kho",
        link: PURCHASE_ORDER_PAGE,
        required: "purchase_order:read",
      },
      {
        key: "sales-orders",
        label: "Đơn bán hàng",
        link: SALES_ORDER_PAGE,
        required: "sale_order:read",
      },
    ],
  },
  {
    key: "debt",
    icon: <IconCreditCard />,
    label: "Công nợ",
    link: DEBT_PAGE,
    children: [],
    required: "debt:read",
  },
  {
    key: "inventory",
    icon: <IconPackage />,
    label: "Tồn kho",
    link: INVENTORY_PAGE,
    children: [],
    required: "inventory:read",
  },
  {
    key: "reports",
    icon: <IconChartBar />,
    label: "Báo cáo",
    link: REPORT_PAGE,
    children: [],
    required: "report:read",
  },
  {
    key: "export-jobs",
    icon: <IconFileExport />,
    label: "Quản lý xuất file",
    link: EXPORT_JOBS_PAGE,
    children: [],
    required: {
      anyOf: [
        "debt:export",
        "inventory:export",
        "partner:export",
        "product:export",
        "purchase_order:export",
        "sale_order:export",
      ],
    },
  },
];

export function isFullAccessRole(roles: UserRoles[]): boolean {
  return roles.some((r) => r === "root" || r === "admin");
}

export function getNavbarItemsByPermissions(ability: AppAbility): NavMenuItem[] {
  return FULL_MENU.flatMap((item) => {
    if (item.children.length === 0) {
      return canAccessRequirement(ability, item.required) ? [item] : [];
    }

    // For parent items with children: filter children first
    const filteredChildren = item.children.filter((c) =>
      canAccessRequirement(ability, c.required),
    );

    // Hide parent if no children passed
    if (filteredChildren.length === 0) return [];

    return [{ ...item, children: filteredChildren }];
  });
}
